import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md text-center">
          <CheckCircle className="h-16 w-16 text-emerald-400 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-3" data-testid="forgot-pw-success-title">Check Your Email</h1>
          <p className="text-zinc-400 text-sm mb-8">
            If an account with <span className="text-[#00E5FF]">{email}</span> exists, we've sent a password reset link. Please check your inbox and spam folder.
          </p>
          <Link to="/login">
            <Button variant="outline" className="border-zinc-800 text-zinc-300 hover:text-[#00E5FF] hover:border-[#00E5FF] rounded-sm" data-testid="back-to-login-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white font-['Outfit'] tracking-tight" data-testid="forgot-pw-title">Forgot Password</h1>
          <p className="text-zinc-500 text-sm mt-2">Enter your email and we'll send you a reset link</p>
        </div>

        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email Address</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="pl-10 h-12 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm"
                  placeholder="you@example.com" data-testid="forgot-pw-email-input" />
              </div>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="forgot-pw-error">{error}</p>}
            <Button type="submit" disabled={loading}
              className="w-full h-12 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="forgot-pw-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Reset Link'}
            </Button>
          </form>

          <p className="text-center text-sm text-zinc-500 mt-6">
            Remember your password? <Link to="/login" className="text-[#00E5FF] hover:underline" data-testid="login-link">Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
